/**
 * ExportAllListsModal - Export all lists as a single backup
 *
 * Creates a JSON backup containing all user lists and their cards.
 */
import type { ReactElement } from 'react';
import { useCallback, useMemo } from 'react';
import { FaDownload } from 'react-icons/fa';
import { IoDownload } from 'react-icons/io5';

import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import ExportPreview from '@/features/import-export/components/ExportPreview';

import { exportMultiListBackup, type ListWithCards } from '@/features/import-export/exporters/mtgcraft';
import { useAllListCards, useLists } from '../hooks/api';

import { copyToClipboard, downloadAsFile } from '../utils/exporters';

// ============================================================================
// Types
// ============================================================================

interface ExportAllListsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ============================================================================
// Component
// ============================================================================

export default function ExportAllListsModal({ isOpen, onClose }: ExportAllListsModalProps): ReactElement {
  const { data: lists = [] } = useLists();
  const { data: allCards = [] } = useAllListCards();

  // Group cards by list
  const listsWithCards = useMemo((): ListWithCards[] => {
    const cardsByList = new Map<string, typeof allCards>();

    for (const card of allCards) {
      const existing = cardsByList.get(card.list_id) ?? [];
      existing.push(card);
      cardsByList.set(card.list_id, existing);
    }

    return lists.map((list) => ({
      list,
      cards: cardsByList.get(list.id) ?? [],
    }));
  }, [lists, allCards]);

  // Generate export content
  const exportContent = useMemo(() => {
    if (lists.length === 0) return '';
    return exportMultiListBackup(listsWithCards);
  }, [lists, listsWithCards]);

  // Stats for display
  const stats = useMemo(() => {
    const totalCards = listsWithCards.reduce((sum, l) => sum + l.cards.length, 0);
    return { listCount: lists.length, cardCount: totalCards };
  }, [lists, listsWithCards]);

  const handleCopy = useCallback(async () => {
    return await copyToClipboard(exportContent);
  }, [exportContent]);

  const handleDownload = useCallback(() => {
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `MtgCraft Lists Backup - ${timestamp}.json`;
    downloadAsFile(exportContent, filename, 'application/json');
  }, [exportContent]);

  // Empty state
  if (lists.length === 0) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Export All Lists" size="md">
        <div className="py-8 text-center">
          <div className="bg-slate mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full">
            <IoDownload className="text-ash h-8 w-8" />
          </div>
          <p className="text-silver">No lists to export</p>
          <p className="text-ash mt-1 text-sm">Create some lists first</p>
          <div className="mt-6">
            <Button text="Close" variant="outlined" onClick={onClose} />
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Export All Lists" size="lg">
      <div className="space-y-5">
        {/* Summary */}
        <div className="bg-slate/50 border-storm rounded-lg border p-4">
          <div className="flex items-center gap-3">
            <div className="bg-aether/20 rounded-lg p-2">
              <FaDownload className="text-aether h-5 w-5" />
            </div>
            <div>
              <p className="text-pearl font-medium">MtgCraft Lists Backup</p>
              <p className="text-ash text-sm">
                {stats.listCount} {stats.listCount === 1 ? 'list' : 'lists'} · {stats.cardCount} total cards
              </p>
            </div>
          </div>
        </div>

        {/* Lists preview */}
        <div className="space-y-2">
          <p className="text-silver text-sm font-medium">Lists included:</p>
          <div className="border-storm max-h-40 divide-y divide-zinc-800 overflow-y-auto rounded-lg border">
            {listsWithCards.map(({ list, cards }) => (
              <div key={list.id} className="flex items-center gap-3 px-3 py-2">
                <span className="text-lg">{list.icon || '📋'}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-pearl truncate text-sm">{list.name}</p>
                </div>
                <span className="text-ash text-xs">{cards.length} cards</span>
                {list.color && <span className="h-3 w-3 shrink-0 rounded" style={{ backgroundColor: list.color }} />}
              </div>
            ))}
          </div>
        </div>

        {/* Preview */}
        <ExportPreview content={exportContent} onCopy={handleCopy} onDownload={handleDownload} />

        {/* Actions */}
        <div className="flex justify-end pt-2">
          <button type="button" onClick={onClose} className="text-ash hover:text-silver px-4 py-2 text-sm transition-colors">
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}
